import React, { useMemo, useState } from "react";
import Button from "@mui/material/Button";
import useDebouncedSearch from "hooks/useDebouncedSearch";
import { useTranslation } from "react-i18next";
import {
  DateRangeFilter,
  EMPTY_DATE_RANGE,
  PageContainer,
  SelectFilter,
} from "@outpave/ds";
import type { DateRangeValue, FilterOption } from "@outpave/ds";
import PaymentStatus from "enums/PaymentStatus";
import useGetParamFromUrl from "hooks/useGetParamFromUrl";
import { Inline } from "components/layout/reusableLayouts";
import { useVendorFilterOptions } from "hooks/useVendorFilterOptions";
import { paymentStatusLabelKey } from "utils/payments/paymentStatus";
import {
  SENT_PAYMENT_METHODS,
  paymentMethodLabelKey,
} from "utils/payments/paymentMethod";
import {
  useDateRangeFilterLabels,
  useDateRangePresets,
} from "utils/dateRangeFilter";
import { useSelectFilterLabels } from "utils/selectFilterLabels";
import {
  PAYMENT_TAB_STATUSES,
  applyStatusPicks,
  statusesOffered,
  statusesToQuery,
  statusPicksOnTab,
} from "utils/payments/paymentFilters";
import PaymentsTable from "components/admin/payments/PaymentsTable";
import PaymentDrawerRouter from "components/admin/payments/PaymentDrawerRouter";
import ViewPaymentDetailsDrawer from "components/admin/payments/ViewPaymentDetailsDrawer";
import { useCheckUserRole } from "hooks/useCheckUserRole";
import Role from "enums/Role";

import { Tooltip, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { Link } from "@mui/material";
import { useQuery } from "@apollo/client";
import { GET_COMPANY } from "../../graphql/company";
export type PaymentFilterCriteriaProps = {
  paymentStatus: PaymentStatus[];
  searchTerm: string;
  contactId?: string;
  /** Matches the scheduled payment date, the Payment Date column. */
  dateRange?: DateRangeValue;
  paymentMethod?: string;
};

const Payments: React.FC = () => {
  const { t } = useTranslation();

  const isAuditor = useCheckUserRole(Role.Auditor, { exact: true });

  const [activeTab, setActiveTab] = useState<number>(0);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const search = useDebouncedSearch(setSearchTerm);
  const [openPaymentRouter, setOpenPaymentRouter] = useState<boolean>(false);
  const [openViewPaymentDetails, setOpenViewPaymentDetails] =
    useState<boolean>(false);

  const [paymentId, setPaymentId] = useState<string>("");
  const [newPayment, setNewPayment] = useState<boolean>(false);
  const [refreshPayments, setRefreshPayments] = useState<number>(0);
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [selectedVendorId, setSelectedVendorId] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<DateRangeValue>(EMPTY_DATE_RANGE);
  const [selectedMethod, setSelectedMethod] = useState<string | null>(null);
  const datePresets = useDateRangePresets();
  const dateLabels = useDateRangeFilterLabels();
  const vendorOptions = useVendorFilterOptions({
    refetchIndex: refreshPayments,
  });
  const filterLabels = useSelectFilterLabels();

  const tabConfigurations = [
    { label: t("Inbox") },
    { label: t("Processing") },
    { label: t("History") },
  ];

  const tabStatuses = PAYMENT_TAB_STATUSES[activeTab];

  const statusOptions: FilterOption[] = useMemo(
    () =>
      statusesOffered(tabStatuses).map((status) => ({
        value: status,
        label: t(paymentStatusLabelKey(status)),
      })),
    [tabStatuses, t],
  );

  const methodOptions: FilterOption[] = useMemo(
    () =>
      SENT_PAYMENT_METHODS.map((method) => ({
        value: method,
        label: t(paymentMethodLabelKey(method) || method),
      })),
    [t],
  );

  const statusPicks = useMemo(
    () => statusPicksOnTab(selectedStatuses, tabStatuses),
    [selectedStatuses, tabStatuses],
  );

  const filterCriteria: PaymentFilterCriteriaProps = useMemo(
    () => ({
      paymentStatus: statusesToQuery(selectedStatuses, tabStatuses),
      searchTerm,
      contactId: selectedVendorId ?? undefined,
      dateRange,
      paymentMethod: selectedMethod ?? undefined,
    }),
    [
      selectedStatuses,
      tabStatuses,
      searchTerm,
      selectedVendorId,
      dateRange,
      selectedMethod,
    ],
  );

  const handleOpenEditDetailsDrawer = (id?: string, newPayment?: boolean) => {
    setPaymentId(id || "");
    setOpenPaymentRouter(true);
    setNewPayment(!!newPayment);
  };

  const handleOpenPaymentDetailsDrawer = (id: string) => {
    setPaymentId(id);
    setOpenViewPaymentDetails(true);
  };

  const handleRefreshPayments = () => {
    setRefreshPayments((prev) => prev + 1);
  };

  const resetParam = useGetParamFromUrl("id", handleOpenPaymentDetailsDrawer);

  const handleClosePaymentDrawerRouter = (
    refresh?: boolean,
    idToOpenPaymentDetails?: string,
  ) => {
    refresh && handleRefreshPayments();
    setOpenPaymentRouter(false);
    setPaymentId(idToOpenPaymentDetails || "");
    if (idToOpenPaymentDetails) {
      setOpenViewPaymentDetails(true);
    }
    resetParam();
    setNewPayment(false);
  };

  const closeViewPaymentDrawer = () => {
    setOpenViewPaymentDetails(false);
    setPaymentId("");
  };

  const { data: companyData, loading: loadingEmail } = useQuery(GET_COMPANY, {
    fetchPolicy: "cache-first",
  });
  const paymentEmail = companyData?.company?.paymentEmail;

  return (
    <PageContainer
      title={t("Payments")}
      primaryAction={
        !isAuditor && (
          <Button
            onClick={() => {
              handleOpenEditDetailsDrawer(undefined, true);
            }}
          >
            {t("Make a Payment")}
          </Button>
        )
      }
      subtitle={
        <Tooltip
          title={
            <Typography variant="body2" sx={{ margin: "var(--space-2)" }}>
              {t("EmptyPaymentEmailDescription")}
            </Typography>
          }
          placement="top"
        >
          <Link
            component={RouterLink}
            to="/admin/settings/company-settings?editMode=true"
            variant="body2"
            underline="none"
          >
            {loadingEmail ? t("Loading") : paymentEmail}
          </Link>
        </Tooltip>
      }
      tabs={{
        items: tabConfigurations.map((tab, index) => ({
          id: String(index),
          label: t(tab.label),
        })),
        activeId: String(activeTab),
        onChange: (id) => setActiveTab(Number(id)),
      }}
      search={search}
      filters={
        <Inline gap="var(--space-3)" align="center">
          <DateRangeFilter
            label={t("Payment Date")}
            presets={datePresets}
            labels={dateLabels}
            value={dateRange}
            onChange={setDateRange}
          />
          <SelectFilter
            label={t("Vendor")}
            options={vendorOptions}
            selected={selectedVendorId}
            onChange={setSelectedVendorId}
            labels={filterLabels}
          />
          <SelectFilter
            multiple
            label={t("Status")}
            options={statusOptions}
            selected={statusPicks}
            onChange={(next) =>
              setSelectedStatuses((previous) =>
                applyStatusPicks(previous, next, tabStatuses),
              )
            }
            labels={filterLabels}
          />
          <SelectFilter
            label={t("Payment Method")}
            options={methodOptions}
            selected={selectedMethod}
            onChange={setSelectedMethod}
            labels={filterLabels}
          />
        </Inline>
      }
    >
      <PaymentsTable
        filterCriteria={filterCriteria}
        refreshPayments={refreshPayments}
        activeTab={activeTab}
        handleRefreshPayments={handleRefreshPayments}
        handleOpenPaymentDetailsDrawer={handleOpenPaymentDetailsDrawer}
        handleOpenEditDetailsDrawer={handleOpenEditDetailsDrawer}
        paymentEmail={paymentEmail}
      />

      <PaymentDrawerRouter
        open={openPaymentRouter}
        paymentId={paymentId}
        handleClosePaymentDrawerRouter={handleClosePaymentDrawerRouter}
        newPayment={newPayment}
      />

      <ViewPaymentDetailsDrawer
        open={openViewPaymentDetails}
        paymentId={paymentId}
        handleClose={closeViewPaymentDrawer}
      />
    </PageContainer>
  );
};

export default Payments;
